import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  Field,
  Icon,
  Page,
  PageHeader,
  TimeField,
} from '@evogard/ui';
import { BILLING_CHANNELS } from '@evogard/contract';
import type { BillingChannel, LadderRange } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { newRowKey, useEditableRows } from '../../lib/editable-rows.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  TIMEZONE_NOTICE,
  LADDER_SOURCE,
  CHANNEL_LABEL,
  trimSeconds,
  channelsNotDispatching,
  dayRangeText,
  useLadder,
} from './common.js';

/**
 * A régua de cobrança da base — as faixas de dias, os canais e as janelas.
 *
 * ## Ela salva a régua INTEIRA, e isso não é gosto
 *
 * `PUT /billing/tiers` substitui a coleção porque **sobreposição só se avalia
 * com o conjunto**: mover uma faixa para o lugar de outra exige que as duas
 * mudem no mesmo instante, e quem julga é o `EXCLUDE USING gist` do banco, que
 * avalia exatamente o conjunto. É o oposto de `editarFase` na retenção, que grava
 * um item por vez para duas abas não apagarem o trabalho uma da outra — aqui esse
 * risco existe e é aceito conscientemente, porque a alternativa é uma régua que o
 * banco recusa por inteiro a cada edição parcial.
 *
 * ## A validação de sobreposição NÃO é reimplementada aqui
 *
 * Quando duas faixas se cruzam, o servidor recusa e a mensagem **nomeia as duas
 * etapas**. Esta tela mostra essa mensagem e não escreve uma própria: duas
 * verdades divergem, e a do banco é a que vale. Uma checagem "para dar um erro
 * melhor" no navegador daria certo em 99% dos casos e, no 1%, diria que está tudo
 * bem sobre uma régua que o banco vai recusar — ou pior, o contrário.
 *
 * ## A régua nasce VAZIA nas duas praças
 *
 * E é o estado de hoje. Sem faixa a esteira não dispara nada e a mesa não tem
 * coluna para agrupar. Uma tabela vazia sem explicação leria como "esta praça
 * decidiu não cobrar"; o que a tela diz é de onde a régua de verdade vem — o
 * banco de produção da esteira legada, pela extração de `cobranca-03`.
 *
 * ## Os três nulos, ditos com todas as letras
 *
 * - **`maxDays` vazio é a faixa final, sem teto** — o `> 60` da esteira legada.
 *   Não se inventa um `9999`: ele apareceria na tela como um limite decidido;
 * - **janela vazia é “sem janela declarada”**, e é o estado de TODA linha
 *   enquanto a extração não vier. Isso **não** é “envia a qualquer hora”: no
 *   servidor, faixa sem janela **não envia**. Sem esta frase alguém salva a régua
 *   achando que ligou a cobrança e não ligou;
 * - **`channels` vazio é “esta etapa não envia”**, e é conteúdo válido — a faixa
 *   final da régua legada é exatamente essa. Não é campo esquecido, e por isso o
 *   `PUT` aceita a lista vazia.
 */
export function TiersScreen() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'billing.tiers', 'editar');
  const query = useLadder();
  const ladder = query.data;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Régua de cobrança"
        back={
          <Link
            to="/billing/desk"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Mesa de cobrança
          </Link>
        }
      >
        Vale para a base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        A outra praça tem a régua dela — mudar aqui não muda lá.
      </PageHeader>

      <Alert tone="informacao" title="Salvar substitui a régua inteira desta base.">
        <p className="mt-1">
          As faixas não são gravadas uma a uma: sobreposição só se avalia com o conjunto,
          e quem julga é o banco. Ao salvar, o que estiver nesta tela passa a ser a régua
          da praça — e se duas faixas cobrirem o mesmo dia, a gravação é recusada inteira,
          com a mensagem nomeando as duas etapas.
        </p>
        <p className="mt-2">
          Não existe rota que apague a régua, de propósito: uma lista vazia é recusada,
          porque é exatamente o corpo que uma tela manda quando o estado dela ainda não
          carregou — e ele desligaria a cobrança da praça toda.
        </p>
      </Alert>

      {!canEdit && (
        <Alert tone="informacao">
          Você vê a régua desta base e não pode alterá-la. Quem administra a plataforma
          concede a ação <strong className="font-medium">editar</strong> em{' '}
          <strong className="font-medium">cobranca.faixas</strong>, em Grupos — e essa
          concessão é a régua toda, não um ajuste de linha.
        </Alert>
      )}

      {query.isError && (
        <Alert tone="erro" title="Não foi possível carregar a régua.">
          {query.error.message}
        </Alert>
      )}

      {ladder?.estado === 'sem_regua' && (
        <Alert tone="alerta" title="Esta praça ainda não tem régua de cobrança.">
          <p className="mt-1">
            Não há nenhuma faixa nesta base. Não é uma falha de carregamento nem um filtro
            escondendo alguma coisa: é o que a casa sabe hoje — e enquanto for assim, a
            esteira não dispara nada e a mesa fica sem colunas.
          </p>
          <p className="mt-2">{LADDER_SOURCE}</p>
        </Alert>
      )}

      {/* Erro sem dado nenhum: o aviso acima é a resposta inteira. Um editor
          vazio logo abaixo dele convidaria a digitar uma régua sobre uma base
          cujo estado a tela não conseguiu ler. */}
      {query.isPending ? (
        <Skeleton />
      ) : ladder ? (
        <LadderEditor
          key={context?.currentTenant.id}
          initial={ladder.estado === 'configurada' ? ladder.tiers : []}
          canEdit={canEdit}
        />
      ) : null}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * O editor
 * ------------------------------------------------------------------------- */

/**
 * Uma linha em edição.
 *
 * Os números são **texto** enquanto se digita: um `number` no estado obriga a
 * escolher o que fazer com o campo vazio e com o `-` sozinho de quem está
 * digitando `-3`, e a escolha silenciosa é `0` — que é um dia da régua, não um
 * "não preenchido".
 *
 * `rowId` existe porque `etapa` é editável: usá-la como `key` do React faria
 * a linha remontar a cada tecla, e o cursor sairia do campo.
 */
interface EditingRow {
  rowId: string;
  step: string;
  order: string;
  minDay: string;
  /** vazio é a faixa final, sem teto */
  maxDay: string;
  channels: BillingChannel[];
  /** vazio é "sem janela declarada" — e sem janela o servidor NÃO envia */
  windowStart: string;
  windowEnd: string;
}

function forRows(tiers: readonly LadderRange[]): EditingRow[] {
  return tiers.map((f) => ({
    rowId: newRowKey('linha'),
    step: f.step,
    order: String(f.order),
    minDay: String(f.minDay),
    maxDay: f.maxDay === null ? '' : String(f.maxDay),
    channels: [...f.channels],
    windowStart: f.windowStart === null ? '' : trimSeconds(f.windowStart),
    windowEnd: f.windowEnd === null ? '' : trimSeconds(f.windowEnd),
  }));
}

/** O que vai para o servidor — sem `rowId`, que é só do React. */
function forBody(rows: readonly EditingRow[]) {
  return rows.map((l) => ({
    step: l.step.trim(),
    order: Number(l.order),
    minDay: Number(l.minDay),
    maxDay: l.maxDay.trim() === '' ? null : Number(l.maxDay),
    channels: l.channels,
    windowStart: l.windowStart === '' ? null : l.windowStart,
    windowEnd: l.windowEnd === '' ? null : l.windowEnd,
  }));
}

function LadderEditor({
  initial,
  canEdit,
}: {
  initial: readonly LadderRange[];
  canEdit: boolean;
}) {
  const { rows, setRows, updated, reseed } = useEditableRows(initial, forRows, forBody);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.billing.saveLadder({ body: { tiers: forBody(rows) } });
      /*
       * A mensagem do servidor, sempre. A recusa mais importante daqui é a
       * sobreposição, e ela vem nomeando as duas etapas que se cruzam — uma
       * mensagem genérica ("não foi possível salvar") jogaria fora justamente
       * a única informação que resolve o problema.
       */
      return ok(r, 200, 'Não foi possível salvar a régua.');
    },
    onSuccess: (tiers) => {
      setError(null);
      setDone(true);
      /*
       * Re-semeia do que o SERVIDOR devolveu, e não do estado local: a régua
       * gravada é a que ele leu de volta do banco, com a ordenação e a
       * normalização dele. Manter o estado local seria a tela afirmando que
       * gravou o que ela tinha, e não o que ficou lá.
       */
      reseed(tiers);
    },
    onError: (e: Error) => {
      setDone(false);
      setError(e.message);
    },
  });

  function change(keyLocal: string, patch: Partial<EditingRow>) {
    setDone(false);
    setRows((current) =>
      current.map((l) => (l.rowId === keyLocal ? { ...l, ...patch } : l)),
    );
  }

  function add() {
    setDone(false);
    setRows((current) => [
      ...current,
      {
        rowId: newRowKey('linha'),
        step: '',
        // a ordem sugerida é a posição na lista, que é derivada e não inventada
        order: String(current.length),
        minDay: '',
        maxDay: '',
        channels: [],
        windowStart: '',
        windowEnd: '',
      },
    ]);
  }

  function discard(keyLocal: string) {
    setDone(false);
    setRows((current) => current.filter((l) => l.rowId !== keyLocal));
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      {error && (
        <Alert tone="erro" title="A régua não foi gravada.">
          {error}
        </Alert>
      )}

      {done && !updated && (
        <Alert tone="sucesso">
          A régua desta base foi substituída pelo que está abaixo.
        </Alert>
      )}

      <Card
        title="Faixas"
        descricao="Cada faixa é um intervalo de dias em torno do vencimento, fechado nos dois lados: um dia exatamente no limite cai nela."
        bleed
      >
        {rows.length === 0 ? (
          <p className="px-4 py-8 text-sm text-texto-suave">
            Nenhuma faixa nesta base. Enquanto for assim, a esteira não dispara nada e a
            mesa não tem coluna para agrupar — e não há o que salvar: a régua vazia é
            recusada pelo servidor.
          </p>
        ) : (
          <ol>
            {rows.map((row, index) => (
              <li
                key={row.rowId}
                className="border-b border-borda px-4 py-4 last:border-b-0"
              >
                <LadderRow
                  row={row}
                  index={index}
                  canEdit={canEdit}
                  onChange={(patch) => change(row.rowId, patch)}
                  toDiscard={() => discard(row.rowId)}
                />
              </li>
            ))}
          </ol>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        {canEdit && (
          <Button variant="contorno" type="button" onClick={add}>
            Acrescentar faixa
          </Button>
        )}
        {canEdit && (
          <Button
            type="submit"
            loading={save.isPending}
            disabled={rows.length === 0 || !updated}
          >
            Salvar a régua desta base
          </Button>
        )}
        {canEdit && updated && (
          <Button
            variant="discreto"
            type="button"
            onClick={() => {
              reseed(initial);
              setError(null);
              setDone(false);
            }}
          >
            Descartar alterações
          </Button>
        )}
        {updated && (
          <span className="text-xs font-medium text-alerta">
            Há alterações que ainda não foram gravadas.
          </span>
        )}
      </div>

      <p className="text-xs text-texto-suave">{TIMEZONE_NOTICE}</p>
      <p className="text-xs text-texto-suave">
        Hoje a casa envia por <strong className="font-medium">e-mail</strong>, e só. O
        WhatsApp pode ser marcado — a extração da régua legada precisa poder registrar o
        que está no ar —, mas nada sai por ele: gravar não é enviar.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * A linha
 * ------------------------------------------------------------------------- */

/** Inteiro digitado, ou `null` enquanto ainda não é um número. */
function whole(text: string): number | null {
  const clean = text.trim();
  if (clean === '') return null;
  const n = Number(clean);
  return Number.isInteger(n) ? n : null;
}

function LadderRow({
  row,
  index,
  canEdit,
  onChange,
  toDiscard,
}: {
  row: EditingRow;
  index: number;
  canEdit: boolean;
  onChange: (patch: Partial<EditingRow>) => void;
  toDiscard: () => void;
}) {
  const minDay = whole(row.minDay);
  const maxDay = row.maxDay.trim() === '' ? null : whole(row.maxDay);
  const withoutCap = row.maxDay.trim() === '';
  const withoutWindow = row.windowStart === '' && row.windowEnd === '';
  const halfWindow = !withoutWindow && (row.windowStart === '' || row.windowEnd === '');
  const notSaem = channelsNotDispatching(row.channels);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-48 flex-1">
          <Field
            label="Etapa"
            required
            maxLength={60}
            disabled={!canEdit}
            value={row.step}
            onChange={(e) => onChange({ step: e.target.value })}
            help={
              index === 0
                ? 'É a identidade da faixa, e é o que cada contato enviado guarda. Trocá-la renomeia a etapa de todo contato já registrado.'
                : undefined
            }
          />
        </div>
        <div className="w-24">
          <Field
            label="Ordem"
            type="number"
            required
            min={0}
            max={999}
            disabled={!canEdit}
            value={row.order}
            onChange={(e) => onChange({ order: e.target.value })}
          />
        </div>
        <div className="w-28">
          <Field
            label="Dia mín."
            type="number"
            required
            min={-365}
            max={3650}
            disabled={!canEdit}
            value={row.minDay}
            onChange={(e) => onChange({ minDay: e.target.value })}
            help={index === 0 ? 'Negativo é antes do vencimento.' : undefined}
          />
        </div>
        <div className="w-28">
          <Field
            label="Dia máx."
            type="number"
            min={-365}
            max={3650}
            disabled={!canEdit}
            value={row.maxDay}
            onChange={(e) => onChange({ maxDay: e.target.value })}
            placeholder="Sem teto"
            help={index === 0 ? 'Vazio é a faixa final, sem teto.' : undefined}
          />
        </div>
        {canEdit && (
          <Button variant="discreto" size="sm" type="button" onClick={toDiscard}>
            Remover
          </Button>
        )}
      </div>

      {/*
       * O intervalo por extenso, atualizado enquanto se digita. É o que impede o
       * `-3` de ser lido como "3 dias de atraso": o operador não deve precisar
       * entender o sinal para conferir a régua que está gravando.
       */}
      <p className="text-sm">
        {minDay === null ? (
          <span className="text-texto-suave">
            Preencha o dia mínimo para ver o intervalo desta faixa.
          </span>
        ) : !withoutCap && maxDay === null ? (
          <span className="text-texto-suave">O dia máximo não é um número inteiro.</span>
        ) : (
          <strong className="font-medium">{dayRangeText(minDay, maxDay)}</strong>
        )}
        {withoutCap && minDay !== null && (
          <span className="text-texto-suave">
            {' '}
            — é a faixa final da régua, e ela não tem teto.
          </span>
        )}
      </p>

      <div className="flex flex-wrap items-start gap-6">
        <fieldset className="min-w-48">
          <legend className="rotulo text-texto-suave">Canais</legend>
          <div className="mt-1.5 flex flex-wrap gap-4">
            {BILLING_CHANNELS.map((channel) => (
              <label key={channel} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={row.channels.includes(channel)}
                  onChange={(e) =>
                    onChange({
                      channels: e.target.checked
                        ? [...row.channels, channel]
                        : row.channels.filter((c) => c !== channel),
                    })
                  }
                  className="size-4 rounded border-borda-forte accent-primaria-texto"
                />
                {CHANNEL_LABEL[channel]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-36">
            <TimeField
              label="Janela início"
              disabled={!canEdit}
              value={row.windowStart}
              onChange={(windowStart) => onChange({ windowStart })}
            />
          </div>
          <div className="w-36">
            <TimeField
              label="Janela fim"
              disabled={!canEdit}
              value={row.windowEnd}
              onChange={(windowEnd) => onChange({ windowEnd })}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/*
         * As três marcas de "esta etapa não envia" são fatos diferentes, e
         * juntá-las numa só apagaria o que a pessoa precisa consertar:
         * sem janela é ausência de decisão, sem canal é decisão, e meia janela
         * é o que o `check` do banco recusa.
         */}
        {withoutWindow && (
          <Badge tone="alerta">Sem janela declarada — o servidor não envia</Badge>
        )}
        {halfWindow && (
          <Badge tone="erro">
            Só uma ponta da janela — o banco recusa as duas juntas
          </Badge>
        )}
        {row.channels.length === 0 && <Badge tone="neutro">Etapa sem canal</Badge>}
        {notSaem.length > 0 && (
          <Badge tone="alerta">
            {notSaem.map((c) => CHANNEL_LABEL[c]).join(' e ')} fica gravado e não sai
          </Badge>
        )}
      </div>

      {withoutWindow && (
        <p className="text-xs text-texto-suave">
          Sem janela não é “envia a qualquer hora”: ninguém declarou quando esta etapa
          pode falar com o associado, então ela não fala. É o estado de toda linha
          enquanto a extração da régua legada não trouxer os horários.
        </p>
      )}
      {row.channels.length === 0 && (
        <p className="text-xs text-texto-suave">
          Nenhum canal marcado é conteúdo válido: esta etapa entra na régua para agrupar a
          mesa e não dispara nada. A faixa final da régua legada é exatamente assim.
        </p>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy>
      <span className="esqueleto block h-16 w-full rounded-borda" />
      <span className="esqueleto block h-40 w-full rounded-borda" />
      <span className="sr-only">Carregando a régua desta base…</span>
    </div>
  );
}
